import { css, cx } from "@emotion/css";
import { ActionButton, ActionButtonType, StepCount } from "@octopusdeploy/design-system-components";
import { borderRadius, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import cn from "classnames";
import { useState } from "react";
import * as React from "react";
import { GreenTickIcon } from "~/areas/projects/components/ProjectStatus/GreenTickIcon";
import { ActiveStatusIcon } from "~/components/ActiveStatusIcon/ActiveStatusIcon";
import { DisabledStatusIcon } from "~/components/ActiveStatusIcon/DisabledStatusIcon";
import type { BusyState } from "~/components/BusyFromPromise/BusyFromPromise";
import DialogLayoutConnect from "~/components/Dialog/DialogLayoutConnect";
import CustomSaveDialogLayout from "~/components/DialogLayout/Custom/CustomSaveDialogLayout";
import { CustomFlexDialogContent, SmallDialogFrame } from "~/components/DialogLayout/Custom/index";
import type { DialogLayoutDispatchProps } from "~/components/DialogLayout/DialogLayout";
import KeyboardHandler, { Key } from "~/components/KeyboardHandler/index";
import { isAllowed, type PermissionCheckProps } from "~/components/PermissionCheck/PermissionCheck";
export interface ProcessTemplateWizardPageProps {
    content: React.ReactNode;
    title: string;
    statusBarLabel: string;
    additionalActions?: JSX.Element[];
    hideAdditionalActionsOnSinglePage?: boolean;
    actionAvailable: boolean;
    singlePageSaveLabel?: string;
    singlePageSavingLabel?: string;
}
export interface ProcessTemplateWizardDialogLayoutProps {
    savePermission?: PermissionCheckProps;
    onSaveClick(): Promise<boolean>;
    errors?: Error[] | null;
    busy?: BusyState | null;
    pages: ProcessTemplateWizardPageProps[];
}
function ProcessTemplateWizardDialogLayoutInternal({ savePermission, onSaveClick, busy, errors, pages, close }: ProcessTemplateWizardDialogLayoutProps & DialogLayoutDispatchProps) {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [dialogResizeKey] = useState(new Date().getDate().toString());
    const pageCount = pages.length;
    const currentPage = pages[currentPageIndex];
    const finalPage = pages[pageCount - 1];
    const saveActionAvailable = finalPage.actionAvailable;
    const saveClick = async () => {
        const result = await onSaveClick();
        if (result) {
            close();
        }
    };
    const isSaveButtonDisabled = (): boolean => {
        const disabledDueToPermission = !!savePermission ? !isAllowed(savePermission) : false;
        return !!(!saveActionAvailable || disabledDueToPermission || busy);
    };
    const canNext = () => currentPage.actionAvailable && currentPageIndex < pageCount - 1;
    const next = () => {
        if (canNext()) {
            setCurrentPageIndex(currentPageIndex + 1);
        }
    };
    const onEscKeyPressed = () => {
        close();
        return true;
    };
    const onCancel = () => {
        close();
    };
    const getSaveLabel = () => {
        if (pageCount === 1) {
            return finalPage.singlePageSaveLabel ?? "Done";
        }
        return "Done";
    };
    const getSaveBusyLabel = () => {
        if (pageCount === 1) {
            return finalPage.singlePageSavingLabel ?? "Saving...";
        }
        return "Saving...";
    };
    const saveActionButton = <ActionButton key="Save" type={ActionButtonType.Save} label={getSaveLabel()} busyLabel={getSaveBusyLabel()} onClick={saveClick} disabled={isSaveButtonDisabled()}/>;
    const cancelActionButton = <ActionButton key="Cancel" label="Cancel" disabled={busy} onClick={onCancel} type={ActionButtonType.Ternary}/>;
    const actions: JSX.Element[] = [];
    const nextActionButton = <ActionButton key="Next" label="Next" disabled={busy || !canNext()} onClick={next} type={ActionButtonType.Primary}/>;
    actions.push(cancelActionButton);
    for (const additionalAction of currentPage.additionalActions ?? []) {
        if (pageCount > 1 || !currentPage.hideAdditionalActionsOnSinglePage) {
            actions.push(additionalAction);
        }
    }
    if (currentPageIndex < pageCount - 1) {
        actions.push(nextActionButton);
    }
    else {
        actions.push(saveActionButton);
    }
    const keyboardRegistrations = [{ key: Key.Esc, onKeyPressed: onEscKeyPressed }];
    const child = pages[currentPageIndex].content;
    return (<CustomSaveDialogLayout frame={SmallDialogFrame} open={true} // This is set to true as it is required as a prop for this component. Although it is not used explicitly in it, it is passed as parameters to the render methods, but it is not needed in our case.
     close={close} onSaveClick={onSaveClick} renderTitle={() => {
            return pageCount > 1 ? <StatusBar items={pages.map((p) => p.statusBarLabel ?? p.title)} currentPageIndex={currentPageIndex}/> : <></>;
        }} renderContent={() => (<CustomFlexDialogContent className={contentStyles.wrapper}>
                    <KeyboardHandler key={dialogResizeKey} registrations={keyboardRegistrations}>
                        <h2 className={contentStyles.title}>{pages[currentPageIndex].title}</h2>
                        {child}
                    </KeyboardHandler>
                </CustomFlexDialogContent>)} renderActions={() => <DialogActions currentStep={currentPageIndex + 1} totalSteps={pageCount} actions={actions}/>} busy={busy ? busy : undefined} errors={errors ? errors : undefined}></CustomSaveDialogLayout>);
}
const contentStyles = {
    wrapper: css({
        margin: `${space[32]} ${space[32]} 0 ${space[32]}`,
    }),
    title: css({
        marginBottom: space[32],
        font: text.heading.medium,
    }),
};
interface StatusBarProps {
    items: string[];
    currentPageIndex: number;
}
interface StatusBarItemProps {
    label: string;
    state: TaskState;
}
type TaskState = "Current" | "Pending" | "Done";
function StatusBarItem({ label, state }: StatusBarItemProps) {
    const StatusIcon = ({ state }: {
        state: TaskState;
    }): JSX.Element => {
        if (state === "Current")
            return <ActiveStatusIcon />;
        if (state === "Pending")
            return <DisabledStatusIcon />;
        if (state === "Done")
            return <GreenTickIcon />;
        return <></>;
    };
    return (<div className={cn(statusBarStyles.statusItemContainer, { [statusBarStyles.complete]: state === "Done", [statusBarStyles.active]: state === "Current" })}>
            <StatusIcon state={state}/>
            <span>{label}</span>
        </div>);
}
function getItemState(index: number, activeItem: number): TaskState {
    if (index < activeItem)
        return "Done";
    if (index > activeItem)
        return "Pending";
    return "Current";
}
function StatusBar({ items, currentPageIndex }: StatusBarProps) {
    return (<div className={statusBarStyles.wrapper}>
            <div className={cx(statusBarStyles.container)}>
                {items.map((item, index) => {
            return <StatusBarItem label={item} state={getItemState(index, currentPageIndex)} key={item}/>;
        })}
            </div>
        </div>);
}
interface DialogActionsProps {
    currentStep: number;
    totalSteps: number;
    actions?: React.ReactNode;
    additionalActions?: React.ReactNode;
}
function DialogActions({ currentStep, totalSteps, actions, additionalActions }: DialogActionsProps) {
    return (<div className={actionStyles.actions}>
            {totalSteps > 1 ? <StepCount currentStep={currentStep} totalSteps={totalSteps} variant="text"/> : <></>}
            <div className={actionStyles.secondaryActions}>{additionalActions}</div>
            <div className={actionStyles.primaryActions}>{actions}</div>
        </div>);
}
const statusBarStyles = {
    statusItemContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        gap: space[8],
        borderRadius: borderRadius.small,
        whiteSpace: "nowrap",
        color: themeTokens.color.text.tertiary,
        padding: `${space[4]} ${space[8]}`,
    }),
    active: css({
        background: themeTokens.color.background.primary.default,
        fontWeight: fontWeight[500],
        "&&": {
            color: themeTokens.color.text.primary,
        },
    }),
    complete: css({
        "&&": {
            color: themeTokens.color.text.primary,
        },
    }),
    isLink: css({
        cursor: "pointer",
        "&&:hover": {
            color: themeTokens.color.text.primary,
            background: themeTokens.color.background.primary.pressed,
        },
    }),
    wrapper: css({
        overflowX: "auto",
        background: themeTokens.color.background.secondary.default,
        borderRadius: borderRadius.small,
        margin: `${space[32]} ${space[32]} 0 ${space[32]}`,
    }),
    container: css({
        font: text.body.regular.xSmall,
        display: "flex",
        gap: space[8],
        alignItems: "center",
        justifyContent: "start",
        padding: `${space[8]} ${space[12]}`,
        userSelect: "none",
    }),
};
const actionStyles = {
    actions: css({
        display: "flex",
        justifyContent: "space-between",
        flex: "0 0 auto",
        flexWrap: "wrap",
        margin: `${space[32]}`,
    }),
    primaryActions: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: space[8],
    }),
    secondaryActions: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: space[8],
    }),
};
const ProcessTemplateWizardDialogLayout = DialogLayoutConnect.to<ProcessTemplateWizardDialogLayoutProps>(ProcessTemplateWizardDialogLayoutInternal);
ProcessTemplateWizardDialogLayout.displayName = "ProcessTemplateWizardDialogLayout";
export default ProcessTemplateWizardDialogLayout;
